import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import PendingActionsIcon from "@mui/icons-material/PendingActions";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import ReplyIcon from "@mui/icons-material/Reply";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  FormLabel,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import PropTypes from "prop-types";
import { useMemo, useState } from "react";

import { ModalTransition } from "../shared/Dialog/Config";

const STATUS_ORDER = [
  "InProcess",
  "Canceled",
  "NotChecked",
  "ReturnToOperator",
  "Completed",
  "Deleted",
];

const STATUS_META = {
  NotChecked: {
    icon: PendingActionsIcon,
    color: "warning.main",
    hint: "هنوز در صف بررسی",
  },
  InProcess: {
    icon: PlayCircleOutlineIcon,
    color: "success.main",
    hint: "تأیید و شروع فرآیند",
  },
  Canceled: {
    icon: CancelOutlinedIcon,
    color: "error.main",
    hint: "رد یا لغو درخواست",
    confirmWarning: "این درخواست لغو می‌شود. در صورت نیاز می‌توانید دلیل را در یادداشت بنویسید.",
  },
  Completed: {
    icon: TaskAltIcon,
    color: "info.main",
    hint: "پایان موفق درخواست",
  },
  Deleted: {
    icon: DeleteOutlineIcon,
    color: "text.secondary",
    hint: "حذف از سیستم",
    confirmWarning: "درخواست به وضعیت حذف‌شده منتقل می‌شود.",
  },
  ReturnToOperator: {
    icon: ReplyIcon,
    color: "primary.main",
    hint: "بازگشت به کارشناس",
  },
};

const DEFAULT_META = {
  icon: CheckCircleOutlineIcon,
  color: "text.secondary",
  hint: "",
};

const normalizeOption = (option) => ({
  value: String(option?.value ?? option?.Value ?? ""),
  name: option?.name ?? option?.Name ?? option?.value ?? option?.Value ?? "",
});

const sortOptions = (options) => {
  const orderIndex = (value) => {
    const idx = STATUS_ORDER.indexOf(value);
    return idx === -1 ? STATUS_ORDER.length : idx;
  };
  return [...options].sort(
    (a, b) => orderIndex(a.value) - orderIndex(b.value)
  );
};

const RequestConfirmChoiceGroup = ({
  label = "وضعیت درخواست",
  value,
  onChange,
  list = [],
  error,
  helperText,
  name,
  noteValue = "",
  onNoteChange,
  noteLabel = "یادداشت",
  compact = false,
}) => {
  const [pendingOption, setPendingOption] = useState(null);
  const [noteDraft, setNoteDraft] = useState("");
  const [noteError, setNoteError] = useState("");

  const options = useMemo(
    () => sortOptions((Array.isArray(list) ? list : []).map(normalizeOption)),
    [list]
  );

  const selectedValue = value != null && value !== "" ? String(value) : "";
  const pendingMeta = pendingOption
    ? STATUS_META[pendingOption.value] || DEFAULT_META
    : null;

  const openConfirm = (option) => {
    if (option.value === selectedValue) {
      return;
    }
    setPendingOption(option);
    setNoteDraft(noteValue || "");
    setNoteError("");
  };

  const closeConfirm = () => {
    setPendingOption(null);
    setNoteDraft("");
    setNoteError("");
  };

  const handleConfirm = () => {
    if (!pendingOption) return;

    const trimmedNote = noteDraft.trim();

    onChange(pendingOption.value);
    if (onNoteChange) {
      onNoteChange(trimmedNote);
    }
    closeConfirm();
  };

  return (
    <>
      <FormControl fullWidth error={error} component="fieldset">
        <FormLabel
          component="legend"
          sx={{ mb: compact ? 1 : 1.5, fontWeight: 600, fontSize: compact ? "0.9rem" : undefined }}
        >
          {label}
        </FormLabel>
        <Box
          role="radiogroup"
          aria-label={label}
          name={name}
          sx={{
            display: "grid",
            gridTemplateColumns: compact
              ? { xs: "1fr", sm: "repeat(2, 1fr)" }
              : {
                  xs: "1fr",
                  sm: "repeat(2, 1fr)",
                  md: "repeat(3, 1fr)",
                },
            gap: compact ? 1 : 1.5,
          }}
        >
          {options.map((option) => {
            const isSelected = selectedValue === option.value;
            const meta = STATUS_META[option.value] || DEFAULT_META;
            const Icon = meta.icon;

            return (
              <Card
                key={option.value}
                variant="outlined"
                sx={{
                  borderWidth: isSelected ? 2 : 1,
                  borderColor: isSelected ? meta.color : "divider",
                  bgcolor: isSelected ? "action.selected" : "background.paper",
                  transition: "border-color 0.15s, box-shadow 0.15s",
                  boxShadow: isSelected ? 2 : 0,
                }}
              >
                <CardActionArea
                  onClick={() => openConfirm(option)}
                  aria-pressed={isSelected}
                  sx={{ height: "100%" }}
                >
                  <Stack
                    direction="row"
                    spacing={compact ? 1 : 1.5}
                    alignItems="center"
                    p={compact ? 0.75 : 1.5}
                  >
                    <Icon
                      sx={{ color: meta.color, flexShrink: 0 }}
                      fontSize={compact ? "small" : "medium"}
                    />
                    <Box sx={{ minWidth: 0 }}>
                      <Typography
                        variant={compact ? "body2" : "subtitle2"}
                        sx={{ fontWeight: 600, lineHeight: 1.3 }}
                      >
                        {option.name}
                      </Typography>
                      {!compact && meta.hint ? (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          display="block"
                        >
                          {meta.hint}
                        </Typography>
                      ) : null}
                    </Box>
                  </Stack>
                </CardActionArea>
              </Card>
            );
          })}
        </Box>
        {helperText ? <FormHelperText>{helperText}</FormHelperText> : null}
      </FormControl>

      <Dialog
        open={Boolean(pendingOption)}
        onClose={closeConfirm}
        TransitionComponent={ModalTransition}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ pb: 1 }}>تأیید تغییر وضعیت</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Typography variant="body1">
              آیا از انتخاب وضعیت{" "}
              <Typography component="span" fontWeight={700} color="primary.main">
                «{pendingOption?.name}»
              </Typography>{" "}
              اطمینان دارید؟
            </Typography>

            {pendingMeta?.confirmWarning ? (
              <Alert severity="warning" icon={<WarningAmberIcon />}>
                {pendingMeta.confirmWarning}
              </Alert>
            ) : null}

            <TextField
              label={noteLabel}
              placeholder="در صورت نیاز توضیح کوتاه بنویسید..."
              value={noteDraft}
              onChange={(event) => {
                setNoteDraft(event.target.value);
                if (noteError) setNoteError("");
              }}
              multiline
              minRows={3}
              fullWidth
              error={Boolean(noteError)}
              helperText={
                noteError || "اختیاری — در صورت ثبت با فرم ذخیره می‌شود."
              }
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={closeConfirm} color="inherit">
            انصراف
          </Button>
          <Button onClick={handleConfirm} variant="contained" color="primary">
            تأیید و اعمال
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

RequestConfirmChoiceGroup.propTypes = {
  label: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func.isRequired,
  list: PropTypes.array,
  error: PropTypes.bool,
  helperText: PropTypes.string,
  name: PropTypes.string,
  noteValue: PropTypes.string,
  onNoteChange: PropTypes.func,
  noteLabel: PropTypes.string,
  compact: PropTypes.bool,
};

export default RequestConfirmChoiceGroup;
